import { css } from "@emotion/css";
import React from "react";
import {
  AriaRadioProps,
  VisuallyHidden,
  mergeProps,
  useFocusRing,
  useRadio,
} from "react-aria";

import { RadioControl } from "./RadioControl";
import { RadioContext } from "./context";
import { InputCard } from "../../../components/inputs/_assets/card/InputCard";
import { FormText } from "../../../components/inputs/_assets/input-wrapper/FormText";
import {
  INPUT_HEIGHT,
  INPUT_WIDTH,
  INPUT_WRAPPER_MIN_HEIGHT,
  INPUT_WRAPPER_MIN_WIDTH,
} from "../../../components/inputs/constants";
import { Text } from "../../../foundations/typography/Text";

export interface RadioProps extends AriaRadioProps {
  subtle?: boolean;
  description?: React.ReactNode;
  name?: string;
  card?: boolean;
  testID?: string;
}

interface ConditionalWrapperProps {
  children: React.ReactNode;
  card?: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  testID?: string;
}

const ConditionalWrapper = (props: ConditionalWrapperProps) => {
  const {
    card,
    children,
    isChecked,
    isDisabled,
    testID = "conditional-wrapper",
  } = props;
  if (card)
    return (
      <InputCard isChecked={isChecked} isDisabled={isDisabled} testID={testID}>
        {children}
      </InputCard>
    );
  else
    return (
      <label
        data-testid={testID}
        className={css({
          display: "flex",
          paddingTop: "var(--space-1)",
          paddingBottom: "var(--space-1)",
        })}
      >
        {children}
      </label>
    );
};

/**
 * @deprecated see RadioRAC
 */
export function Radio(props: RadioProps) {
  const { subtle, description, children, card, testID = "radio" } = props;
  const state = React.useContext(RadioContext);
  const ref = React.useRef<HTMLInputElement>(null);
  const { inputProps, isDisabled, isSelected } = useRadio(props, state, ref);

  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <ConditionalWrapper
      card={card}
      isChecked={isSelected}
      isDisabled={isDisabled}
      testID={`${testID}-wrapper`}
    >
      <VisuallyHidden>
        <input
          {...mergeProps(inputProps, focusProps)}
          data-testid={`${testID}-radio`}
          ref={ref}
        />
      </VisuallyHidden>
      <RadioControl
        isSelected={isSelected}
        isDisabled={isDisabled}
        isFocusVisible={isFocusVisible}
      />
      <div
        className={css(styles.textContent, {
          paddingLeft: !card ? "var(--space-2)" : undefined,
          gap: !card ? "var(--space-1)" : undefined,
        })}
      >
        <Text
          style={subtle ? "input/helper" : "input/label"}
          color={subtle ? "text-weak" : "text-base"}
          className={styles.labelText}
        >
          {children}
        </Text>
        {description && (
          <FormText
            className={css(styles.description, {
              "& a": {
                color: "var(--text-weak)",
              },
            })}
          >
            {description}
          </FormText>
        )}
      </div>
    </ConditionalWrapper>
  );
}

const styles = {
  inputWrapper: css({
    height: INPUT_WRAPPER_MIN_HEIGHT,
    minWidth: INPUT_WRAPPER_MIN_WIDTH,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  }),
  labelText: css({
    padding: 0,
    margin: 0,
  }),
  description: css({
    padding: 0,
    margin: 0,
  }),
  input: css({
    appearance: "none",
    width: INPUT_WIDTH,
    height: INPUT_HEIGHT,
    borderRadius: "100%",
    position: "relative",
    margin: 0,
    "&:checked:before": css({
      height: 0,
      width: 0,
      content: '""',
      position: "absolute",
      margin: "auto",
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      overflow: "hidden",
      border: "4px solid transparent",
      borderRadius: "100%",
    }),
  }),
  textContent: css({
    width: "100%",
    flexDirection: "column",
    display: "flex",
  }),
};
